import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './WishlistPage.css';

interface WishlistProduct {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  discount?: number;
  image: string;
  rating: number;
  brand: string;
  weight: string;
  category: string;
}

const WishlistPage: React.FC = () => {
  const navigate = useNavigate();
  const [wishlistItems, setWishlistItems] = useState<WishlistProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadWishlist();
  }, []);

  const loadWishlist = async () => {
    try {
      setIsLoading(true);
      // Load from localStorage or API
      const savedWishlist = localStorage.getItem('wishlist');
      if (savedWishlist) {
        setWishlistItems(JSON.parse(savedWishlist));
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromWishlist = (productId: string) => {
    const updated = wishlistItems.filter(item => item.id !== productId);
    setWishlistItems(updated);
    localStorage.setItem('wishlist', JSON.stringify(updated));
  };

  const addToCart = async (product: WishlistProduct) => {
    try {
      // Navigate to browse all with product search to find shops selling this product
      // Then user can select shop and add to cart
      navigate('/browse-all', {
        state: {
          searchProduct: product.name,
          productId: product.id
        }
      });
    } catch (error) {
      console.error('Error adding to cart from wishlist:', error);
      alert('Please select a shop to add this product to cart. Navigate to Browse All page.');
    }
  };

  return (
    <div className="wishlist-page">
      <div className="wishlist-body-container">
        <div className="wishlist-header">
          <button className="back-btn" onClick={() => navigate('/app')}>
            ← Back to Dashboard
          </button>
          <h1>❤️ My Wishlist</h1>
          <div className="wishlist-count">{wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'}</div>
        </div>

        {isLoading ? (
          <div className="loading-state">Loading wishlist...</div>
        ) : wishlistItems.length === 0 ? (
          <div className="empty-wishlist">
            <div className="empty-icon">❤️</div>
            <h2>No wishlist items yet</h2>
            <p>Start exploring products and add them to your wishlist!</p>
            <button className="explore-btn" onClick={() => navigate('/browse-all')}>
              Browse Products
            </button>
          </div>
        ) : (
          <div className="wishlist-grid">
          {wishlistItems.map(product => (
            <div key={product.id} className="wishlist-item">
              <div className="item-image">
                {product.image.startsWith('http') || product.image.startsWith('/') ? (
                  <img src={product.image} alt={product.name} />
                ) : (
                  <span className="product-emoji">{product.image}</span>
                )}
                <button 
                  className="remove-btn"
                  onClick={() => removeFromWishlist(product.id)}
                  aria-label="Remove from wishlist"
                >
                  ❌
                </button>
              </div>
              <div className="item-info">
                <div className="item-brand">{product.brand}</div>
                <div className="item-name">{product.name}</div>
                <div className="item-weight">{product.weight}</div>
                <div className="item-rating">⭐ {product.rating}</div>
                <div className="item-price">
                  {product.originalPrice && (
                    <span className="original-price">₹{product.originalPrice}</span>
                  )}
                  <span className="current-price">₹{product.price}</span>
                </div>
                <button 
                  className="add-to-cart-btn"
                  onClick={() => addToCart(product)}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;









